import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "./api";

const STORAGE_KEY = "atelier_locale";
const DEFAULT_LOCALE = "az";
const SUPPORTED_LOCALES = ["az", "en"];
const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    // A stored "en" must survive a reload — the previous check discarded it.
    return SUPPORTED_LOCALES.includes(stored) ? stored : DEFAULT_LOCALE;
  });
  const [messages, setMessages] = useState({});

  // Keep the document language in step with the UI so assistive tech and
  // search engines read the page in the language it is actually written in.
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  useEffect(() => {
    let active = true;
    api.get(`/i18n/${locale}.json`)
      .then((r) => { if (active) setMessages(r.data || {}); })
      .catch((err) => {
        console.error("[atelier] i18n load failed", err);
        if (active) setMessages({});
      });
    return () => { active = false; };
  }, [locale]);

  const value = useMemo(() => {
    const setLocale = (next) => {
      localStorage.setItem(STORAGE_KEY, next);
      setLocaleState(next);
    };
    const t = (key, fallback, vars) => {
      let text = messages[key] || fallback || key;
      if (vars) {
        Object.entries(vars).forEach(([name, value]) => {
          text = text.replaceAll(`{${name}}`, String(value));
        });
      }
      return text;
    };
    return { locale, setLocale, t };
  }, [locale, messages]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) return { locale: DEFAULT_LOCALE, setLocale: () => {}, t: (_key, fallback) => fallback };
  return ctx;
}
